import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { listTasks, mapApiTaskToUi } from '../../../services/tasks.js';
import { apiErrorStatus } from './apiErrorStatus.js';

/**
 * The Tasks of one record, grouped (CT-5722). Same shape as the other list
 * hooks: a race guard, serialized keys for the object inputs, a memoized return.
 * `refresh` is quiet, for after a row action.
 *
 * The Tasks tab stays mounted across records, so the list is scoped to the
 * record open NOW: a full load starts empty, and a `refresh` captured for the
 * previous record (a row action there that answers late) does nothing.
 *
 * `refresh` is stable and always re-reads the query ON SCREEN: a row action
 * keeps the `refresh` of the moment it was clicked, and the user may have
 * searched or filtered since — re-reading that older query would cancel the
 * one they see and paint the old list back (Bugbot, #568).
 */
export function useTasks({ entityType, entityId, filters, search } = {}) {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [status, setStatus] = useState(null);
  const fetchIdRef = useRef(0);
  const recordKey = `${entityType}/${entityId}`;
  const openRecordRef = useRef(recordKey);

  const filtersKey = JSON.stringify(filters ?? {});
  const searchKey = JSON.stringify(search ?? null);

  const fetchData = useCallback(
    async ({ quiet = false } = {}) => {
      if (!entityType || !entityId) return;
      if (`${entityType}/${entityId}` !== openRecordRef.current) return;
      const requestId = ++fetchIdRef.current;
      if (!quiet) {
        setLoading(true);
        setError(null);
        setStatus(null);
        setGroups([]);
      }

      try {
        const parsedSearch = JSON.parse(searchKey);
        const { body } = await listTasks({
          entityType,
          entityId,
          filters: JSON.parse(filtersKey),
          ...(parsedSearch ? { search: parsedSearch } : {}),
        });
        if (requestId !== fetchIdRef.current) return;
        setGroups(
          (body?.groups ?? []).map((group) => ({
            key: group.key ?? '',
            total: group.total ?? group.items?.length ?? 0,
            items: (group.items ?? []).map(mapApiTaskToUi),
          })),
        );
      } catch (err) {
        if (requestId !== fetchIdRef.current || quiet) return;
        setGroups([]);
        setStatus(apiErrorStatus(err));
        setError(err?.message ?? 'Failed to load the tasks');
      } finally {
        if (requestId === fetchIdRef.current) setLoading(false);
      }
    },
    [entityType, entityId, filtersKey, searchKey],
  );

  const refetch = useCallback(() => fetchData(), [fetchData]);
  const fetchDataRef = useRef(fetchData);
  useEffect(() => {
    fetchDataRef.current = fetchData;
  }, [fetchData]);
  const refresh = useCallback(() => fetchDataRef.current({ quiet: true }), []);

  // Declared BEFORE the fetch effect, so the new record is "open" when it loads.
  useEffect(() => {
    openRecordRef.current = recordKey;
  }, [recordKey]);

  useEffect(() => {
    fetchData();
    return () => {
      fetchIdRef.current += 1;
    };
  }, [fetchData]);

  return useMemo(
    () => ({ groups, loading, error, accessDenied: status === 403, refetch, refresh }),
    [groups, loading, error, status, refetch, refresh],
  );
}

export default useTasks;
